import React, { useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { WalletToTradingAccountTransfer } from 'api/Wallet/Types/WalletToTradingAccountTransfer'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { TriangleAlert } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

const WalletToTradingAccountTransferFormUI: React.FC<
  FormikProps<WalletToTradingAccountTransferFormValues> &
    OuterProps &
    WalletToTradingAccountTransferFormContainerProps
> = (props) => {
  const {
    client,
    values,
    isSubmitting,
    errors,
    setFieldValue,
    walletTooltipTitle,
    tradingAccountTooltipTitle,
    hasWalletRestrictions,
    hasTradingAccountRestrictions,
    onSubmitForm,
  } = props

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const walletWarning =
    hasWalletRestrictions && walletTooltipTitle ? (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert size={14} />
        </TooltipTrigger>
        <TooltipContent side='top'>{`Restrictions: ${walletTooltipTitle}`}</TooltipContent>
      </Tooltip>
    ) : undefined

  const tradingAccountWarning =
    hasTradingAccountRestrictions && tradingAccountTooltipTitle ? (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert size={14} />
        </TooltipTrigger>
        <TooltipContent side='top'>{`Restrictions: ${tradingAccountTooltipTitle}`}</TooltipContent>
      </Tooltip>
    ) : undefined

  const walletRightAddon =
    values.walletId || walletWarning ? (
      <>
        {values.walletId && (
          <Link
            to={generatePath(links.recordsWallets.detailsUrl as string, {
              id: values.walletId,
              slug: 'details',
            })}
            showIcon
          />
        )}
        {walletWarning}
      </>
    ) : undefined

  const tradingAccountRightAddon =
    values.tradingAccountId || tradingAccountWarning ? (
      <>
        {values.tradingAccountId && (
          <Link
            to={generatePath(links.recordsTradingAccounts.detailsUrl as string, {
              id: values.tradingAccountId,
              slug: 'details',
            })}
            showIcon
          />
        )}
        {tradingAccountWarning}
      </>
    ) : undefined

  return (
    <Loading isLoading={props.isLoading}>
      <Form onSubmit={onSubmitForm} data-test='transaction-form'>
        <DataGrid columns={1}>
          <DataColumn>
            <DataCard title='Transfer' columns={2}>
              <FormReadonlyField
                name='client'
                label='Client'
                value={values.client ?? ''}
                dataTest='client-field'
                rightAddon={
                  <>
                    <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                      Select
                    </Button>
                    {client?.id && (
                      <Link
                        to={generatePath(links.recordsClients.detailsUrl as string, {
                          id: client.id,
                          slug: 'details',
                        })}
                        showIcon
                      />
                    )}
                  </>
                }
              />
              <FormSelectField
                name='walletId'
                label='Wallet'
                value={values.walletId ?? undefined}
                options={props.wallets.map((wallet) => ({
                  id: wallet.id,
                  name: `${wallet.name}-balance:${wallet.balance}`,
                }))}
                error={errors.walletId}
                onChange={(value) => {
                  const nextWalletId = value ? String(value) : ''
                  setFieldValue('walletId', nextWalletId)
                  props.onSelectWallet(nextWalletId)
                }}
                rightAddon={walletRightAddon}
              />
              <FormSelectField
                name='tradingAccountId'
                label='Trading Account'
                value={values.tradingAccountId ?? undefined}
                options={(props.tradingAccounts || []).map((account) => ({
                  id: account.id,
                  name: `${account.name}-${account.accountGroup.name}-balance: ${account.platformOverview.balance} ${account.currency.id}`,
                }))}
                error={errors.tradingAccountId}
                showSearch
                onChange={(value) => {
                  const nextTradingAccountId = value ? String(value) : ''
                  setFieldValue('tradingAccountId', nextTradingAccountId)
                  props.onSelectTradingAccount(nextTradingAccountId)
                }}
                rightAddon={tradingAccountRightAddon}
              />
              <FormField
                name='amount'
                label='Amount'
                type='number'
                error={errors.amount}
                dataTest='amount-field'
                rightAddon={
                  props.currency ? (
                    <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                      {props.currency}
                    </span>
                  ) : undefined
                }
              />
              <FormTextareaField
                name='comment'
                label='Comment'
                rows={3}
                maxLength={130}
                dataTest='comment-field'
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
        <div className='mt-4 flex justify-center'>
          <Button disabled={isSubmitting} data-test='submit' type='submit'>
            Submit
          </Button>
        </div>
      </Form>
    </Loading>
  )
}

interface WalletToTradingAccountTransferFormContainerProps {
  onSubmitForm: (event: React.FormEvent<HTMLFormElement>) => void
}

const WalletToTradingAccountTransferFormContainer: React.FC<
  FormikProps<WalletToTradingAccountTransferFormValues> & OuterProps
> = (props) => {
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [isRestrictionModalOpen, setRestrictionModalOpen] = useState(false)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = await props.validateForm(props.values)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    if (props.hasWalletRestrictions || props.hasTradingAccountRestrictions) {
      setRestrictionModalOpen(true)
      return
    }

    setConfirmationModalOpen(true)
  }

  const handleRestrictionSubmit = () => {
    setConfirmationModalOpen(true)
    setRestrictionModalOpen(false)
  }

  const handleConfirmationSubmit = async () => {
    try {
      await props.submitForm()
      setConfirmationModalOpen(false)
    } catch (error) {
      console.error(error)
      setConfirmationModalOpen(false)
    }

    props.resetForm()
    props.resetAll()
  }

  return (
    <>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={() => setConfirmationModalOpen(false)}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to transfer fund from the wallet {props.selectedWallet?.name} to the
                trading account {props.selectedTradingAccount?.name}. The parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {props.client?.name}
                </div>
                <div id='field'>
                  <strong>From Wallet:</strong> {props.selectedWallet?.name}
                </div>
                <div id='field'>
                  <strong>To Trading Account:</strong> {props.selectedTradingAccount?.name}
                </div>
                <div id='field'>
                  <strong>Debit Amount:</strong> {props.values.amount}{' '}
                  {props.selectedWallet?.currency.id}
                </div>
              </div>
            </div>
          }
        />
      )}
      {isRestrictionModalOpen && (
        <ConfirmationModal
          yesButtonText='Yes, proceed'
          onYes={handleRestrictionSubmit}
          onCancel={() => setRestrictionModalOpen(false)}
          visible={isRestrictionModalOpen}
          renderTitle={
            <div>There is a restriction in place which agent can acknowledge before proceeding</div>
          }
        />
      )}
      <WalletToTradingAccountTransferFormUI {...props} onSubmitForm={handleSubmitForm} />
    </>
  )
}

interface WalletToTradingAccountTransferFormValues {
  client: string | null
  walletId: string | null
  tradingAccountId: string | null
  amount: number
  comment: string | null
}

interface OuterProps {
  isLoading: boolean
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  client: ClientDto | null
  wallets: WalletDto[]
  tradingAccounts: TradingAccountDto[]
  onSubmit(values: FormSubmitValues<WalletToTradingAccountTransfer>): Promise<void>
  onSelectWallet(walletId: string): void
  onSelectTradingAccount(tradingAccountId: string): void
  resetAll(): void
  setClient(): void
  selectedWallet?: WalletDto
  selectedTradingAccount?: TradingAccountDto
  tradingAccountTooltipTitle?: string
  walletTooltipTitle?: string
  hasWalletRestrictions: boolean
  hasTradingAccountRestrictions: boolean
}

const dataToSubmit = (
  values: WalletToTradingAccountTransferFormValues
): FormSubmitValues<WalletToTradingAccountTransfer> => ({
  Amount: toNumber(values.amount),
  Comment: values.comment,
  FromWalletId: values.walletId,
  ToTaId: values.tradingAccountId,
})

export const WalletToTradingAccountTransferForm = withFormik<
  OuterProps,
  WalletToTradingAccountTransferFormValues
>({
  mapPropsToValues: ({ client, selectedWallet, selectedTradingAccount }) => ({
    client: client?.name || null,
    walletId: selectedWallet ? selectedWallet.id : '',
    tradingAccountId: selectedTradingAccount ? selectedTradingAccount.id : null,
    amount: 0,
    comment: null,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } catch (error) {
      console.error(error)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<WalletToTradingAccountTransferFormValues> = {}
    const selectedWallet = props.wallets.find((wallet) => wallet.id === values.walletId)

    if (!values.tradingAccountId) {
      errors.tradingAccountId = 'Required'
    }

    if (!values.walletId) {
      errors.walletId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    if (selectedWallet && toNumber(selectedWallet.balance) <= 0) {
      errors.walletId = 'Must be greater than 0'
    }

    if (selectedWallet && selectedWallet.balance < values.amount) {
      errors.amount = 'Amount exceed available wallet balance'
    }

    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(WalletToTradingAccountTransferFormContainer)
